import React, { useState, useMemo, useEffect, memo, useCallback } from "react";
import {useDispatch, useSelector} from "react-redux";
import {addSelectItems, closeTab, ElementsAction} from "store/actions/table";
import {searchInlines, showToast, toArray} from "utils/helpers";
import classNames from "classnames";
import "./DashboardInfoTable.scss";

import TableInfoFooter from "components/uiComponents/TableInfoFooter";
import TableInfoHeader from "components/uiComponents/TableInfoHeader";
import SearchInput from "components/uiComponents/SearchInput";
import DownloadInfoTableExcel from "components/DownloadInfoTableExcel";
import TableInfoRow from "components/uiComponents/TableInfoRow";
import SaveAsCollection from "../SaveAsCollection";
import SaveCollection from "../SaveCollection";
import { indeterminateCheckboxTypes as checkboxTypes } from "../uiComponents/IndeterminateCheckbox";
import UnassignFromCollection from "../UnassignFromCollection";
import DropDownAddCollection from "../uiComponents/DropDownAddCollection";
import {tabType} from "../../utils/data";
import {useDashboardState} from "../DashboardContext";
import { collectionDelete } from "../../store/actions/collections";
import { isolateElements } from "../../utils/builder/IScheduleLogic";

const DashboardInfoTable = ({ table }) => {
  const dispatch = useDispatch();

  const { compare } = useSelector((state) => state.viewer);
  const { header, lineElements, lines, totals, units } = table;
  const { tabs } = useSelector((state) => state.table);
  const state = useSelector((state) => state);

  const [selectedItems, setSelectedItems] = useState(table.selectItems || {});
  const [indexs, setIndexs] = useState([]);
  const [headerArr, setHeaderArr] = useState(header);
  const [groupsSameItems, setGroupsSameItems] = useState([]);
  const [footer, setFooter] = useState(totals);
  const [sorting, setSorting] = useState({});

  useEffect(() => {
    if(!compare.isModeCompare && Object.values(selectedItems).length) {
      dispatch(ElementsAction(Object.values(selectedItems).flat(2)));
    }
  }, [compare.isModeCompare]);

  useEffect(async () => {
    if(!lineElements.length && table.type === tabType.unsaved) {
      const tab = tabs.find((tab) => table.id === tab.id);
      dispatch(closeTab(tab));
    }
    if(!lineElements.length && table.type !== tabType.unsaved) {
      try {
        await dispatch(collectionDelete({...table.collection, folder: table.folder, tabs: tabs, id: table.id}));
        showToast(true,"Selection set was deleted");
      } catch (error) {
        showToast(false, error);
      }
    }
  }, [lineElements]);

  useEffect(() => {
    const sortObj = {};

    header?.forEach(item => {
      sortObj[item] = {
        isActive: false,
        isDescending: true,
      }
    });

    setSorting(sortObj);
  }, [header])

  const uniqLines = useCallback(() => {
    if (!lines || !lineElements) {
      return [];
    }

    const newArr = lines.map((el, index) => {
      const newEl = {
        lineElements: lineElements[index],
        lines: el,
      };

      return newEl;
    });

    return newArr;
  }, [lines, lineElements]);

  const selectedTotals = useMemo(() => {
    const selectedTotalsArr = [];

    const linesFiltered = lines?.filter((item, i) => {
      let isInsideSelectedList = false;

      for (let key in selectedItems) {
        const keyStringArr = key.split('/');
        let keyString = keyStringArr[0];

        if (keyStringArr.length > 1) {
          keyStringArr.pop(); 
          keyString = keyStringArr.join('/');
        } else {
          keyString = keyStringArr[0];
        }

        if (item[0] == keyString) {
          isInsideSelectedList = true;
        }
      }

      return isInsideSelectedList;
    });

    linesFiltered?.forEach((item, i) => {
      item.forEach((el, index) => {
        if (typeof el === 'string') {
          if (typeof selectedTotalsArr[index] === 'number') {
            selectedTotalsArr[index] = selectedTotalsArr[index];
          } else {
            selectedTotalsArr[index] = '';
          }
        } else if (typeof el === 'number') {
          if (selectedTotalsArr[index] || selectedTotalsArr[index] === 0) {
            selectedTotalsArr[index] = selectedTotalsArr[index] + el;
          } else if (!selectedTotalsArr[index] && (el || el === 0)) {
            selectedTotalsArr[index] = el;
          } else if ((selectedTotalsArr[index] || selectedTotalsArr[index] === 0) && !el) {
            selectedTotalsArr[index] = selectedTotalsArr[index];
          } else {
            selectedTotalsArr[index] = 0;
          }
        }
      });
    });

    return selectedTotalsArr;
  }, [selectedItems, lines]);

  const filterFun = (arr) => {
    if (!arr) {
      return [];
    }

    const newArr = arr.filter((el, index) => {
      const condition = !indexs.some((element) => {
        return element === index;
      });

      return condition;
    });

    return newArr;
  };

  const lineId = (lines, lineElements) =>
    `${lines[0]}/${lineElements.join("")}`;

  const getCheckboxStatus = () => {

    const countSelectedItems = Object.keys(table.selectItems || {}).length;

    return groupsSameItems.length === countSelectedItems ? checkboxTypes.checked :
           countSelectedItems === 0 ? checkboxTypes.unchecked :
           checkboxTypes.indeterminate;
  }

  const handleSearch = (e) => {
    const value = e.target.value;

    if (!lines) return;

    setGroupsSameItems((prevState) => {
      //  new arr with changed obj{lines,lineEl}(changed lines)
      const neewArr = uniqLines().map((el) => ({
        ...el,
        lines: filterFun(el.lines),
      }));

      const searchedArr = searchInlines(neewArr, value);

      return searchedArr;
    });
  };

  const onChangeCheckbox = (isChecked) => {

    if (!isChecked) {
      let obj = {};

      groupsSameItems.map(({ lines, lineElements }) => {
        const id = lineId(lines, lineElements);

        return (obj[id] = lineElements);
      });

      setSelectedItems((prevState) => {
        const tempOb = { ...prevState, ...obj };

        dispatch(ElementsAction(toArray(tempOb)));
        dispatch(addSelectItems({selectItems: tempOb, id: table.id}));

        return tempOb;
      });
    } else {
      setSelectedItems((prevState) => {
        const tempOb = {};

        for (const key in prevState) {
          const condition = groupsSameItems.some(
            (el) => lineId(el.lines, el.lineElements) === key
          );

          if (!condition) {
            tempOb[key] = prevState[key];
          }
        }

        dispatch(ElementsAction(toArray(tempOb)));
        dispatch(addSelectItems({selectItems: tempOb, id: table.id}));

        return tempOb;
      });
    }
  };

    const dispatchData = (obj) => {
        const selectElementsInTable = toArray(obj);

        if (selectElementsInTable.length > 0) {
          dispatch(ElementsAction(selectElementsInTable));
        } else {
          isolateElements(table.lineElements.flat());
        }
  }

  useEffect(() => {
    setSelectedItems(table.selectItems || {});
    setGroupsSameItems(uniqLines());
    setHeaderArr(header);
    setFooter(totals);
    setIndexs([]);
  }, [lineElements, lines, header, totals]);

  const handleRowCheckChange = useCallback((isChecked, lineElements, id) => {
    setSelectedItems((prevState) => {
      let obj = {};

      if (isChecked) {
        obj = { ...prevState, [id]: lineElements };
      } else {
        let newObj = {};

        for (const key in prevState) {
          if (key !== id) {
            newObj[key] = prevState[key];
          }
        }

        obj = newObj;
      }
      dispatch(addSelectItems({selectItems: obj, id: table.id}));
      dispatchData(obj);

      return obj;
    });
  }, [table]);

  const unitsArr = useMemo(() => {
    return units?.map(item => {
      if (item) {
        const unitParts = item.split('^');

        return {
          unit: unitParts[0],
          subUnit: unitParts[1],
        };
      } else {
        return null;
      }
    })
  }, [units]);

  const rows = useMemo(() => {
    if (groupsSameItems) {
      let sortingName = null;

      for (let key in sorting) {
        if (sorting[key].isActive) {
          sortingName = key;
        }
      }

      let sortedRows = groupsSameItems;

      if (sortingName) {
        const linesIndex = header?.findIndex(item => item === sortingName);
        const lineRow = groupsSameItems.find(row => {
          if (row.lines[linesIndex] || row.lines[linesIndex] === 0) {
            return true;
          }
        });
        const line = lineRow?.lines[linesIndex];

        let sortingType = '';

        if (typeof line === 'number') sortingType = 'number';
        if (typeof line === 'string') sortingType = 'string';

        sortedRows = groupsSameItems.sort((a, b) => {
          if (sortingType === 'string') {
            if (sorting[sortingName].isDescending) {
              return b?.lines[linesIndex]?.localeCompare(a?.lines[linesIndex]);
            } else {
              return a?.lines[linesIndex]?.localeCompare(b?.lines[linesIndex]);
            }
          } else if (sortingType === 'number') {
            if (sorting[sortingName].isDescending) {
              return Number(b?.lines[linesIndex]) - Number(a?.lines[linesIndex]);
            } else {
              return Number(a?.lines[linesIndex]) - Number(b?.lines[linesIndex]);
            }
          } else {
            return a;
          }
        });
      }

      return (
        sortedRows &&
        sortedRows.map(({ lines, lineElements }) => {
          const id = lineId(lines, lineElements);

          return (
            <TableInfoRow
              lineElements={lineElements}
              key={id}
              lines={lines}
              unitsArr={unitsArr}
              id={id}
              onCheckChange={handleRowCheckChange}
              checked={selectedItems[id] ? selectedItems[id] : false}
            />
          );
        })
      );
    }
  }, [groupsSameItems, selectedItems, sorting, unitsArr]);

  useEffect(() => {
    if (indexs.length > 0) {
      setGroupsSameItems((prevState) =>
        uniqLines().map((el) => ({ ...el, lines: filterFun(el.lines) }))
      );

      setFooter((prevState) => filterFun(totals));
      setHeaderArr((prevState) => filterFun(header));
    }
  }, [indexs]);

  const handleSortChange = (name) => {
    const sortObj = {};

    header?.forEach(item => {
      if (item === name) {
        sortObj[item] = {
          isActive: true,
          isDescending: !sorting[item]?.isDescending,
        };
      } else {
        sortObj[item] = {
          isActive: false,
          isDescending: true,
        };
      }
    });

    setSorting(sortObj);
  };

  const isSelect = () => {
    return Object.keys(selectedItems).length !== 0;
  }

  const getTableForSelectedElements= () => {
    let lineElements = Object.values(selectedItems);

    return {...table, lineElements, deleteCollection: lineElements.length === table.lineElements.length};
  }

  const deleteSelectElements = () => {
    setSelectedItems({});
  }

  return (
    <div className="dashboard-info-table" >
      <div className="dashboard-info-table__buttons">
        <div
          className="dashboard-info-table__buttons_search"
        >
          <SearchInput
            size="md"
            type="text"
            name="sideSearch"
            className="find"
            placeholder="Search..."
            width={250}
            isVisible={true}
            onChange={handleSearch}
            isWithoutCloseButton
          />
        </div>
        <div className="dashboard-info-table__buttons-right">
          {
            (table.type === tabType.unsaved && !isSelect()) &&
            <SaveCollection tableInfo={table} />
          }
          {
            (isSelect() && table.type !== tabType.report) &&
            <UnassignFromCollection deleteSelectElements={deleteSelectElements} tableInfo={getTableForSelectedElements()} />
          }
          {
            isSelect() &&
            <SaveAsCollection tableInfo={getTableForSelectedElements()} />
          }
          {
            isSelect() &&
            <DropDownAddCollection tableInfo={getTableForSelectedElements()}/>
          }
          {!!header && !!groupsSameItems && !!footer && (

            <DownloadInfoTableExcel
              header={header}
              lines={groupsSameItems.map((el) => el.lines)}
              fileName={table.folder ? `${table.folder} - ${table.name}` : "BIMSearch report"}
              totals={footer}
              isCheckFlag={isSelect()}
            />
          )}
        </div>
      </div>

      <div
        className={classNames({
          "dashboard-info-table__container": true,
        })}
      >
        <div className="table">
          {!!headerArr && !!headerArr.length &&
            <TableInfoHeader
              header={headerArr}
              onChangeCheckbox={onChangeCheckbox}
              status={getCheckboxStatus()}
              sorting={sorting}
              onSortChange={handleSortChange}
            />
          }

          {!!rows && !!rows.length &&
            <div
              className={classNames({
                "table-body": true,
              })}
            >
              {rows}
            </div>
          }

          {!!footer && !!rows && !!rows.length &&
            <TableInfoFooter
              totals={footer}
              rows={rows}
              checkList={selectedItems}
              selectedTotals={selectedTotals}
              header={headerArr}
              unitsArr={unitsArr}
            />
          }
        </div>
      </div>
    </div>
  );
};

export default memo(DashboardInfoTable);
